/**
 * Frontend telemetry logger (Phase 2).
 *
 * Отправляет на backend /api/telemetry/front batch'ами каждые 5 сек или
 * immediate на error. Сохраняет request_id корреляцию с backend-логами.
 *
 * Использование:
 *   import { logAction, logError, loggedAxios, getSessionId } from './frontLogger';
 *   logAction('click', { target: 'button_sync' });
 *   const api = loggedAxios();   // axios with request_id + network logging
 *   try { await api.get('/api/x'); } catch (e) { logError(e, { ctx: '...' }); }
 */
import axios from 'axios';

const API_URL = (() => {
  if (process.env.NODE_ENV === 'production') return '';
  return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
})();

const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE = 100;
const SESSION_KEY = 'tonmm_session_id';

// --- session id ---

function _makeId() {
  return (
    Date.now().toString(36) +
    Math.random().toString(36).substring(2, 10)
  );
}

export function getSessionId() {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = _makeId();
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return 'no-storage';
  }
}

export function newRequestId() {
  return _makeId();
}

// --- event queue ---

const _queue = [];
let _flushTimer = null;

function _enqueue(evt) {
  _queue.push({
    ts: new Date().toISOString(),
    session_id: getSessionId(),
    page: typeof window !== 'undefined' ? window.location.pathname : undefined,
    ...evt,
  });
  if (_queue.length >= MAX_BATCH_SIZE) {
    _flush();
  } else if (!_flushTimer) {
    _flushTimer = setTimeout(_flush, FLUSH_INTERVAL_MS);
  }
}

async function _flush() {
  if (_flushTimer) {
    clearTimeout(_flushTimer);
    _flushTimer = null;
  }
  if (_queue.length === 0) return;
  const batch = _queue.splice(0, _queue.length);
  try {
    await axios.post(
      `${API_URL}/api/telemetry/front`,
      { events: batch },
      { timeout: 5000, headers: { 'Content-Type': 'application/json' } }
    );
  } catch (err) {
    // Never block user; swallow silently after warn.
    // eslint-disable-next-line no-console
    console.warn('[frontLogger] flush failed, dropping', batch.length, 'events', err?.message);
  }
}

// Flush on unload (best-effort через keepalive fetch)
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (_queue.length === 0) return;
    try {
      const url = `${API_URL}/api/telemetry/front`;
      const payload = JSON.stringify({ events: _queue.splice(0) });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
      } else {
        // sync fallback
        fetch(url, { method: 'POST', body: payload, keepalive: true, headers: { 'Content-Type': 'application/json' } });
      }
    } catch { /* swallow */ }
  });
  // Periodic flush independent of enqueue.
  setInterval(() => { if (_queue.length) _flush(); }, FLUSH_INTERVAL_MS);
}

// --- Public API ---

/**
 * Log a user action (click, input, navigation, etc).
 * @param {string} event_type — click | input | navigation | page_view | ...
 * @param {object} [fields] — { target, extra }
 */
export function logAction(event_type, fields = {}) {
  const { target, extra } = fields;
  _enqueue({ event_type, target, extra });
}

/**
 * Log an error caught by caller.
 * @param {Error|any} err
 * @param {object} [ctx]
 */
export function logError(err, ctx = {}) {
  const message = err?.message || String(err);
  _enqueue({
    event_type: 'error',
    error_msg: message.slice(0, 1024),
    extra: { stack: (err?.stack || '').slice(0, 1024), ...ctx },
  });
  // Flush right away on error
  _flush();
}

/**
 * Wrap axios with request_id header + automatic network event logging.
 */
export function loggedAxios(baseConfig = {}) {
  const instance = axios.create({
    baseURL: API_URL || '',
    ...baseConfig,
  });
  instance.interceptors.request.use((cfg) => {
    const rid = newRequestId();
    cfg.headers = { ...(cfg.headers || {}), 'X-Request-ID': rid };
    cfg.metadata = { rid, start: performance.now() };
    return cfg;
  });
  instance.interceptors.response.use(
    (resp) => {
      const { rid, start } = resp.config.metadata || {};
      _enqueue({
        event_type: 'network',
        request_id: rid,
        url: resp.config.url,
        http_method: (resp.config.method || 'get').toUpperCase(),
        http_status: resp.status,
        duration_ms: start ? Math.round(performance.now() - start) : undefined,
      });
      return resp;
    },
    (err) => {
      const { rid, start } = err.config?.metadata || {};
      _enqueue({
        event_type: 'network',
        request_id: rid,
        url: err.config?.url,
        http_method: (err.config?.method || 'get').toUpperCase(),
        http_status: err.response?.status || 0,
        duration_ms: start ? Math.round(performance.now() - start) : undefined,
        error_msg: (err.message || '').slice(0, 512),
      });
      // do not swallow
      return Promise.reject(err);
    }
  );
  return instance;
}

/**
 * Global uncaught-error handlers (вызвать один раз в index.js).
 */
export function installGlobalErrorHandlers() {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (e) => {
    logError(e.error || new Error(e.message || 'window.error'), {
      ctx: 'window.onerror',
      filename: e.filename,
      lineno: e.lineno,
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    logError(e.reason || new Error('unhandledrejection'), {
      ctx: 'unhandledrejection',
    });
  });
}

// On import — log page_view once for the current URL (debug-friendly).
if (typeof window !== 'undefined') {
  // Delay a tick so app can set sessionStorage etc.
  setTimeout(() => {
    try { logAction('page_view', { target: window.location.pathname }); } catch { /* swallow */ }
  }, 100);
}
